/**
 * Admin → Organisations: each brand's identity, sending mailbox, legal entity and switches,
 * plus a readiness checklist showing what the brand still needs before it can be used.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Stack,
  Switch,
  Typography,
} from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { organisationSettings, type Brand, type BrandInput } from '../services/organisationSettings';
import {
  brandReadiness,
  loadReadinessData,
  type ReadinessData,
} from '../services/organisationReadiness';

type Draft = {
  tradingName: string;
  senderEmail: string;
  senderName: string;
  websiteUrl: string;
  portalLogoPath: string;
  legalEntityName: string;
  companyNumber: string;
  vatNumber: string;
  registeredOffice: string;
  directoryOrgId: string;
  sortOrder: string;
  isSelectable: boolean;
  freeagentSyncEnabled: boolean;
};

function toDraft(brand: Brand): Draft {
  return {
    tradingName: brand.tradingName,
    senderEmail: brand.senderEmail || '',
    senderName: brand.senderName || '',
    websiteUrl: brand.websiteUrl || '',
    portalLogoPath: brand.portalLogoPath || '',
    legalEntityName: brand.legalEntityName || '',
    companyNumber: brand.companyNumber || '',
    vatNumber: brand.vatNumber || '',
    registeredOffice: brand.registeredOffice || '',
    directoryOrgId: brand.directoryOrgId != null ? String(brand.directoryOrgId) : '',
    sortOrder: String(brand.sortOrder),
    isSelectable: brand.isSelectable,
    freeagentSyncEnabled: brand.freeagentSyncEnabled,
  };
}

function fromDraft(draft: Draft): BrandInput {
  const directory = draft.directoryOrgId.trim();
  const sort = Number(draft.sortOrder);
  return {
    tradingName: draft.tradingName,
    senderEmail: draft.senderEmail,
    senderName: draft.senderName,
    websiteUrl: draft.websiteUrl,
    portalLogoPath: draft.portalLogoPath,
    legalEntityName: draft.legalEntityName,
    companyNumber: draft.companyNumber,
    vatNumber: draft.vatNumber,
    registeredOffice: draft.registeredOffice,
    directoryOrgId: directory ? Number(directory) : null,
    sortOrder: Number.isFinite(sort) ? sort : 100,
    isSelectable: draft.isSelectable,
    freeagentSyncEnabled: draft.freeagentSyncEnabled,
  };
}

const TEXT_FIELDS: Array<{ key: keyof Draft; label: string; help?: string; wide?: boolean }> = [
  { key: 'tradingName', label: 'Trading name', help: 'Shown to customers on emails and the portal.' },
  { key: 'senderEmail', label: 'Sending mailbox', help: 'Customer emails for this brand are sent from here.' },
  { key: 'senderName', label: 'Sender name', help: 'Defaults to the trading name.' },
  { key: 'websiteUrl', label: 'Website', help: 'Linked from the customer portal.' },
  { key: 'portalLogoPath', label: 'Portal logo', help: 'Path or URL of the logo on the customer portal.', wide: true },
];

const LEGAL_FIELDS: Array<{ key: keyof Draft; label: string; wide?: boolean }> = [
  { key: 'legalEntityName', label: 'Legal entity' },
  { key: 'companyNumber', label: 'Company number' },
  { key: 'vatNumber', label: 'VAT number' },
  { key: 'registeredOffice', label: 'Registered office', wide: true },
];

function ReadinessList({ brand, brands, data }: { brand: Brand; brands: Brand[]; data: ReadinessData | null }) {
  if (!data) return <Typography level="body-sm" color="neutral">Checking…</Typography>;
  const checks = brandReadiness(brand, brands, data);
  const outstanding = checks.filter((check) => !check.ok).length;
  return (
    <Stack spacing={0.75}>
      <Typography level="title-sm">
        Readiness {outstanding === 0 ? '· all set' : `· ${outstanding} to do`}
      </Typography>
      {checks.map((check) => (
        <Stack key={check.key} direction="row" spacing={1} alignItems="flex-start">
          <Chip size="sm" variant="soft" color={check.ok ? 'success' : 'warning'} sx={{ minWidth: 28, justifyContent: 'center' }}>
            {check.ok ? '✓' : '!'}
          </Chip>
          <Box sx={{ minWidth: 0 }}>
            <Typography level="body-sm" fontWeight="md">{check.label}</Typography>
            {check.detail && (
              <Typography level="body-xs" color="neutral" sx={{ wordBreak: 'break-word' }}>{check.detail}</Typography>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

function BrandCard({
  brand,
  brands,
  readiness,
  onSaved,
}: {
  brand: Brand;
  brands: Brand[];
  readiness: ReadinessData | null;
  onSaved: () => void;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(() => toDraft(brand));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => { setDraft(toDraft(brand)); }, [brand]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(brand));
  const set = (key: keyof Draft, value: string | boolean) => setDraft((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await organisationSettings.update(brand.id, fromDraft(draft));
      setMessage({ tone: 'success', text: 'Saved.' });
      onSaved();
    } catch (err: any) {
      setMessage({ tone: 'danger', text: err?.message || 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await organisationSettings.setDefault(brand.id);
      onSaved();
    } catch (err: any) {
      setMessage({ tone: 'danger', text: err?.message || 'Could not change the default.' });
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof Draft, label: string, help?: string) => (
    <FormControl size="sm">
      <FormLabel>{label}</FormLabel>
      <Input value={String(draft[key])} onChange={(event) => set(key, event.target.value)} />
      {help && <FormHelperText>{help}</FormHelperText>}
    </FormControl>
  );

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography level="title-lg">{brand.name}</Typography>
          <Chip size="sm" variant="outlined">{brand.code}</Chip>
          {brand.isDefault && <Chip size="sm" color="primary" variant="soft">Default</Chip>}
          {!brand.isSelectable && <Chip size="sm" color="neutral" variant="soft">Retired</Chip>}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="sm" variant="plain" onClick={() => navigate(`/admin?tab=pdf-export&org=${encodeURIComponent(brand.code)}`)}>
            PDF settings
          </Button>
          <Button size="sm" variant="plain" onClick={() => navigate(`/admin?tab=email-templates&org=${encodeURIComponent(brand.name)}`)}>
            Email templates
          </Button>
          <Button size="sm" variant="plain" onClick={() => navigate('/docs/settings/sequences')}>
            Numbering
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Grid container spacing={3}>
        <Grid xs={12} md={8}>
          <Stack spacing={2}>
            <Grid container spacing={1.5}>
              {TEXT_FIELDS.map((f) => (
                <Grid key={f.key} xs={12} sm={f.wide ? 12 : 6}>{field(f.key, f.label, f.help)}</Grid>
              ))}
            </Grid>

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Legal entity</Typography>
              <Grid container spacing={1.5}>
                {LEGAL_FIELDS.map((f) => (
                  <Grid key={f.key} xs={12} sm={f.wide ? 12 : 4}>{field(f.key, f.label)}</Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Behaviour</Typography>
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid xs={12} sm={6}>
                  <FormControl size="sm" orientation="horizontal" sx={{ gap: 1 }}>
                    <Switch checked={draft.isSelectable} onChange={(event) => set('isSelectable', event.target.checked)} disabled={brand.isDefault} />
                    <Box>
                      <FormLabel>Available for new work</FormLabel>
                      <FormHelperText>Offered when creating workspaces, quotes and customers.</FormHelperText>
                    </Box>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl size="sm" orientation="horizontal" sx={{ gap: 1 }}>
                    <Switch checked={draft.freeagentSyncEnabled} onChange={(event) => set('freeagentSyncEnabled', event.target.checked)} />
                    <Box>
                      <FormLabel>Send to FreeAgent</FormLabel>
                      <FormHelperText>Allow this brand's invoices and bills into FreeAgent.</FormHelperText>
                    </Box>
                  </FormControl>
                </Grid>
                <Grid xs={6} sm={3}>
                  {field('directoryOrgId', 'Directory id', 'Customer directory link.')}
                </Grid>
                <Grid xs={6} sm={3}>
                  {field('sortOrder', 'Order')}
                </Grid>
              </Grid>
            </Box>

            {message && <Alert size="sm" color={message.tone} variant="soft">{message.text}</Alert>}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="sm" onClick={save} loading={saving} disabled={!dirty}>Save</Button>
              {dirty && (
                <Button size="sm" variant="plain" color="neutral" onClick={() => setDraft(toDraft(brand))} disabled={saving}>
                  Discard
                </Button>
              )}
              {!brand.isDefault && (
                <Button size="sm" variant="outlined" color="neutral" onClick={makeDefault} disabled={saving || dirty}>
                  Make default
                </Button>
              )}
            </Stack>
          </Stack>
        </Grid>
        <Grid xs={12} md={4}>
          <ReadinessList brand={brand} brands={brands} data={readiness} />
        </Grid>
      </Grid>
    </Card>
  );
}

export default function OrganisationsPanel() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [readiness, setReadiness] = useState<ReadinessData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, data] = await Promise.all([organisationSettings.list(true), loadReadinessData()]);
      setBrands(list);
      setReadiness(data);
    } catch (err: any) {
      setError(err?.message || 'Could not load organisations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const settingsMissing = brands.length > 0 && brands.every((brand) => !brand.legalEntityName && !brand.senderEmail);
  const visible = brands.filter((brand) => showRetired || brand.isSelectable);
  const retiredCount = brands.length - brands.filter((brand) => brand.isSelectable).length;

  return (
    <Stack spacing={2}>
      <Typography level="body-md" color="neutral">
        Each organisation is a brand with its own letterhead, wording, mailbox and numbering. The readiness list shows what a
        brand still needs before customer documents and emails can go out under it.
      </Typography>
      {error && <Alert color="danger" variant="soft">{error}</Alert>}
      {settingsMissing && (
        <Alert color="warning" variant="soft">
          Organisation settings aren't in the database yet, so these values are the old built-in ones and can't be saved.
          Apply the organisation settings migration first.
        </Alert>
      )}
      {retiredCount > 0 && (
        <FormControl size="sm" orientation="horizontal" sx={{ gap: 1 }}>
          <Switch checked={showRetired} onChange={(event) => setShowRetired(event.target.checked)} />
          <FormLabel>Show retired brands ({retiredCount})</FormLabel>
        </FormControl>
      )}
      {loading && <Typography level="body-sm">Loading…</Typography>}
      {visible.map((brand) => (
        <BrandCard key={brand.id} brand={brand} brands={brands} readiness={readiness} onSaved={load} />
      ))}
    </Stack>
  );
}
